<template>
  <VCard
  ref="card"
  class="h-100"
  >
    <VRow
        ref="addressSelectionWrapper"
        class="m-0 w-100"
        :class="{
        'h-100': !$vuetify.breakpoint.xs ||  (!$vuetify.breakpoint.xs && !getIsPoiListNotEmpty)
      }"
    >
      <VCol
          class="p-0 map-container"
          cols="12"
          :lg="getIsPoiListNotEmpty ? 8 : 12"
          :md="getIsPoiListNotEmpty ? 8 : 12"
          :sm="getIsPoiListNotEmpty ? 7 : 12"
          :xs="12"
          :style="getMapContainerStyle"
      >
        <VMap
            ref="map"
            :isLoading="isLoading"
            :mapKey="mapKey"
            :placeMarkers="getPoiMarkers"
            :selectedPoiType="selectedPoiType"
            :mapCenter="getMapCenter"
            :zoom="zoom"
            :selectedLocation="selectedLocation"
            :selectedPoi="getSelectedPoi"
            :polygonList="getPolygonList"
            :ops-zone-color="opsZoneColor"
            :userMarkers="getUserMarkers"
            :isDeparturePlaceFilled="isDeparturePlaceFilled"
            :isArrivalPlaceFilled="isArrivalPlaceFilled"
            :currentStep="currentStep"
            :additionalHeightForMap="additionalHeightForMap"
            @onMapClick="setUserMarker"
            @selectMarker="selectMarker"
            @updateZoom="updateZoom"
            @selectPlace="selectPlace"
            @recalculateHeight="$emit('recalculateHeight')"
            @map-ready="$emit('map-ready')"
        >
          <template #prepend-inner>
            <div class="w-100 px-3">
              <AddressSearch
                  :selectedAddress="selectedLocation.address"
                  @selectAddress="onAddressSelect"
              />
            </div>
          </template>
          <template #prepend-inner-after>
            <div
                v-if="!!error"
                data-test="directionDialogErrorMessageContainer"
                class="w-100 px-3 mt-3"
            >
              <div class="error-container px-4 py-2 d-flex align-center">
                <div class="icon-container">
                  <VIcon color="error">
                    mdi-alert-octagon
                  </VIcon>
                </div>
                <div class="message-container pl-2">
                  <span class="body-1">
                    {{ error }}
                  </span>
                </div>
              </div>
            </div>
          </template>
          <template #append-inner>
            <div
                v-if="(!getIsPoiListNotEmpty && isLocationSelected) || ($vuetify.breakpoint.xs && isLocationSelected)"
                class="action-container w-100 px-3 d-flex justify-content-center"
            >
              <VBtn
                  id="set-location-button"
                  ref="setLocationButton"
                  class="w-50"
                  color="brandingPrimary"
                  dark
                  @click="setLocation"
              >
                <span class="button">
                  {{ $t('general.next') }}
                </span>
              </VBtn>
            </div>
          </template>
        </VMap>
      </VCol>
      <VCol
          v-if="getIsPoiListNotEmpty"
          class="p-0 d-flex flex-column justify-content-between shadow-none poi-list-container"
          cols="12"
          lg="4"
          md="4"
          sm="5"
          xs="12"
          :class="{
          'h-100': !$vuetify.breakpoint.xs,
          'pb-2': $vuetify.breakpoint.xs
        }"
          :style="{
          'height': $vuetify.breakpoint.xs ? '100%' : '100%',
        }"
      >
        <PoiSelection
            ref="poiSelection"
            class="overflow-auto"
            :poiList="poiList"
            :selectedPoi="getSelectedPoi"
            :selectedPoiStopId="selectedLocation.selectedPoiStopId"
            :selectedPoiType="selectedPoiType"
            @updateSelectedPoiType="updateSelectedPoiType"
            @updateSelectedPoiStopId="selectedPoiStopStopId"
            @selectPlace="selectPlace"
        />
        <div
            v-if="!$vuetify.breakpoint.xs"
            class="p-4"
        >
          <VBtn
              ref="setLocationButton"
              class="w-100"
              color="brandingPrimary"
              :class="{'disabled': error || !isLocationSelected}"
              data-test="setLocationButton"
              dark
              @click="setLocation"
          >
            <span class="button">
              {{ $t('general.next') }}
            </span>
          </VBtn>
        </div>
      </VCol>
    </VRow>
  </VCard>
</template>
<script lang="ts">
export default {
  name: 'AddressSelection',
};
</script>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import * as L from 'leaflet';
import pointInPolygon from 'point-in-polygon';
import ripple from '@slg/web-customer-shared/src/utils/ripple';
import VMap from '@slg/web-customer-shared/src/components/bookingForm/directionSelection/vMap/VMap.vue';
import { markerTypesMeta } from '@slg/web-customer-shared/src/utils/iconResolver';
import { determiningSelectedPoint } from '@slg/web-customer-shared/src/providers/MapProvider';
import { useI18n, useVuetify } from '../../../../composables';
import PoiSelection from './PoiSelection.vue';
import AddressSearch from './AddressSearch.vue';

const props = defineProps({
  isLoading: { type: Boolean, default: false },
  poiListWithoutGrouping: { type: Array, default: () => [] },
  poiList: { type: Object, default: () => {} },
  pickedLocation: { type: Object, default: () => {} },
  currentStep: { type: String, default: '' },
  additionalHeightForMap: { type: Number, default: 0 },
  error: { type: String, default: '' },
  opsZoneList: { type: Array, default: () => [] },
  isArrivalPlaceFilled: { type: Boolean, default: false },
  isDeparturePlaceFilled: { type: Boolean, default: false },
  communeCoordinates: { type: Array, default: () => [] },
  isNightCardsSupport: { type: Boolean, default: false },
  additionalMapZonesColor: { type: String, default: '' },
  opsZoneColor: { type: String, default: '' },
  opsZoneMapFill: { type: String, default: '' },
  opsZoneMapStroke: { type: String, default: '' },
  communeMapFill: { type: String, default: '' },
  communeMapStroke: { type: String, default: '' },
});

const emit = defineEmits(['recalculateHeight', 'setError', 'setLocation', 'updatePoiId', 'updatePoiType', 'map-ready']);

const { t } = useI18n();
const vuetify = useVuetify();

const map = ref(null);
const setLocationButton = ref(null);
const poiSelection = ref(null);
const addressSelectionWrapper = ref(null);

const mapKey = ref(new Date().toString());
const zoom = ref(9);
const poiId = ref('');
const selectedPoiType = ref('');
const selectedLocation = reactive({
  address: '',
  lat: '',
  lon: '',
  type: '',
  isPoi: false,
  selectedPoiStopId: '',
});
const prevLocation = ref({});

const getCorrectHeightValue = computed(() => (props.additionalHeightForMap > 0 ? props.additionalHeightForMap : 0));
const getMapContainerStyle = computed(() => (vuetify.breakpoint.xs
  ? { height: `${400 + getCorrectHeightValue.value}px` }
  : { height: '100%' }));
const getUserMarkers = computed(() => {
  if (!selectedLocation.isPoi) {
    return [{
      valid: !props.error,
      lat: selectedLocation.lat,
      lon: selectedLocation.lon,
    }];
  }
  return [];
});
const getIsPoiListNotEmpty = computed(() => !!Object.keys(props.poiList).map((key) => props.poiList[key].items).flat(Infinity).length);
const useCustomMapZoneStyles = computed(() => !!(
  props.opsZoneMapFill
  || props.opsZoneMapStroke
  || props.communeMapFill
  || props.communeMapStroke
));
const getSelectedStop = computed(() => {
  if (selectedLocation.selectedPoiStopId) {
    const allStops = Object.keys(props.poiList)
      .map((poiType) => props.poiList[poiType].items.map((subItems) => subItems.subItems))
      .flat(Infinity);
    return allStops.find((item) => item.id === selectedLocation.selectedPoiStopId);
  }
  return null;
});
const getSelectedPoi = computed(() => props.poiListWithoutGrouping?.find((poi) => poi.stops.find((poiStop) => poiStop.poiStopId === getSelectedStop.value?.id)) || {});
const getPolygonList = computed(() => {
  if (useCustomMapZoneStyles.value) {
    const zonePolygons = props.opsZoneList
      .map((zone) => ({
        latlngs: zone?.opsZoneArea.map((marker) => [marker.latitude, marker.longitude]),
        fill: props.opsZoneMapFill,
        stroke: props.opsZoneMapStroke,
      }));
    if (props.isNightCardsSupport && props.communeCoordinates?.length) {
      zonePolygons.push({
        latlngs: props.communeCoordinates.map((marker) => [marker.latitude, marker.longitude]),
        fill: props.communeMapFill,
        stroke: props.communeMapStroke,
      });
    }
    return zonePolygons;
  }
  const zonePolygons = props.opsZoneList
    .map((zone) => ({ latlngs: zone?.opsZoneArea.map((marker) => [marker.latitude, marker.longitude]) }));
  if (props.isNightCardsSupport && props.communeCoordinates?.length) {
    zonePolygons.push({
      latlngs: props.communeCoordinates.map((marker) => [marker.latitude, marker.longitude]),
      color: props.additionalMapZonesColor,
    });
  }
  return zonePolygons;
});
const isLocationSelected = computed(() => selectedLocation?.address && selectedLocation?.lat && selectedLocation?.lon);
const getMapCenter = computed(() => {
  if (isLocationSelected.value) {
    if (getSelectedStop.value) {
      return [+getSelectedStop.value.lat, +getSelectedStop.value.lon];
    }
    return [+selectedLocation.lat, +selectedLocation.lon];
  }
  if (!props.opsZoneList.length) {
    return [0, 0];
  }
  const listOfCentersForEachOpsZone = props.opsZoneList
    .map((zone) => L.featureGroup(zone?.opsZoneArea
      .map((marker) => L.marker([marker.latitude, marker.longitude]))
      .flat(Infinity)).getBounds().getCenter());
  const groupOfCenters = L.featureGroup(listOfCentersForEachOpsZone
    .map((marker) => L.marker([marker.lat, marker.lng])))
    .getBounds()
    .getCenter();
  return [groupOfCenters.lat, groupOfCenters.lng];
});
const getPoiMarkers = computed(() => {
  let allStops = Object.keys(props.poiList)
    .map((poiType) => props.poiList[poiType].items.map((gates) => gates.subItems[0]));
  if (getSelectedStop.value) {
    allStops = Object.keys(props.poiList)
      .map((poiType) => props.poiList[poiType].items.map((gates) => {
        if (gates.subItems[0].parentInd === getSelectedStop.value.parentInd) {
          return gates.subItems;
        }
        return gates.subItems[0];
      }));
  }
  return allStops.flat(Infinity).map((stop) => ({
    poiId: stop.parentInd,
    id: stop.id,
    activeStop: stop.id === selectedLocation.selectedPoiStopId,
    activePoi: stop.parentInd === getSelectedPoi.value.pointOfInterestId,
    tooltip: stop.address,
    position: [+stop.lat, +stop.lon],
    type: stop.type,
  })).filter((item) => item.type === selectedPoiType.value || item.activeStop || item.activePoi || !selectedPoiType.value);
});

selectedLocation.selectedPoiStopId = props.pickedLocation.poiStopId;
selectedLocation.type = props.pickedLocation.type;
selectedLocation.lat = props.pickedLocation.lat;
selectedLocation.lon = props.pickedLocation.lon;
selectedLocation.isPoi = !!props.pickedLocation.poiStopId;
prevLocation.value = { ...selectedLocation };
setTimeout(() => {
  selectedLocation.address = props.pickedLocation.address;
}, 500);

onMounted(() => {
  emit('recalculateHeight');
});

function invalidateMapSize() {
  nextTick(() => {
    map.value?.invalidateMapSize();
  });
}
function setMapFocused(focused) {
  if (map.value) {
    map.value.isFocused = focused;
  }
}
function getWrapperClientHeight() {
  const wrapper = addressSelectionWrapper.value;
  if (!wrapper) {
    return 0;
  }
  return wrapper.$el ? wrapper.$el.clientHeight : wrapper.clientHeight;
}
function coordsInsideAreaChecker(coords, polygonList) {
  return !!polygonList.find((polygon) => pointInPolygon(coords, polygon));
}
function coordsInsidePoiChecker(coords, poiList) {
  return poiList.find((poi) => pointInPolygon(coords, poi.polygon));
}
function setPoi(poi) {
  selectedLocation.address = poi.address;
  selectedLocation.type = poi.type;
  selectedLocation.lat = poi.lat;
  selectedLocation.lon = poi.lon;
  selectedLocation.selectedPoiStopId = poi.id;
  selectedLocation.isPoi = true;
}
function selectAddress(data) {
  if (selectedLocation.address === data.address) {
    ripple(setLocationButton.value.$el);
  }
  selectedLocation.address = data.address;
  selectedLocation.type = markerTypesMeta.other;
  selectedLocation.lat = data.lat;
  selectedLocation.lon = data.lng || data.lon;
  selectedLocation.selectedPoiStopId = '';
  selectedLocation.isPoi = false;
}
function selectedPoiStopStopId(id) {
  if (selectedLocation.selectedPoiStopId === id) {
    return;
  }
  emit('updatePoiId', id);
  const selectedPoi = props.poiListWithoutGrouping.find((poi) => poi.stops.find((poiStop) => poiStop.poiStopId === id));
  selectedPoiType.value = selectedPoi?.type;
  emit('updatePoiType', selectedPoi?.type);
  selectedLocation.selectedPoiStopId = id;
  setPoi(getSelectedStop.value);
  emit('setError', '');
}
async function setUserMarker(locationInfo, isSelectedFromSearchDropdown = false) {
  const poiList = props.poiListWithoutGrouping.map((poi) => ({
    polygon: poi.polygon.map((marker) => [+marker.latitude, +marker.longitude]),
    id: poi.pointOfInterestId,
  }));
  const selectedPoi = coordsInsidePoiChecker([locationInfo.latlng.lat, locationInfo.latlng.lng], poiList);
  if (selectedPoi) {
    const selectedStop = props.poiListWithoutGrouping.find((poi) => poi.pointOfInterestId === selectedPoi.id);
    selectedPoiStopStopId(selectedStop.stops[0].poiStopId);
    emit('setError', '');
    return;
  }
  const opsZoneAreas = props.opsZoneList.map((opsZone) => opsZone.opsZoneArea.map((marker) => [+marker.latitude, +marker.longitude]));
  const selectedLoc = isSelectedFromSearchDropdown ? locationInfo : await determiningSelectedPoint(locationInfo.latlng.lat, locationInfo.latlng.lng);
  const isMarkerInsideZone = coordsInsideAreaChecker([selectedLoc.lat, selectedLoc.lng || selectedLoc.lon], opsZoneAreas);
  selectAddress(selectedLoc);
  if (!isMarkerInsideZone) {
    const error = t('bookingForm.errors.outOfOpsZone');
    emit('setError', error);
  } else {
    emit('setError', '');
  }
}
function onAddressSelect(locationInfo) {
  const data = {
    ...locationInfo,
    latlng: {
      lat: locationInfo.lat,
      lng: locationInfo.lon,
    },
  };
  setUserMarker(data, true);
}
function setLocation() {
  const dataToSet = {
    address: selectedLocation?.address,
    lat: selectedLocation?.lat,
    lon: selectedLocation?.lon,
    type: selectedLocation?.type,
    isPoi: !!selectedLocation?.selectedPoiStopId,
    poiStopId: selectedLocation?.selectedPoiStopId,
  };
  emit('setLocation', dataToSet);
}
function updateSelectedPoiType(e) {
  selectedPoiType.value = e;
  if (!vuetify.breakpoint.xs && e) {
    scrollToPoiSelection(e);
  }
}
function scrollToPoiSelection(type, e, position = 'center') {
  if (vuetify.breakpoint.xs) {
    updateSelectedPoiType(type);
  }
  if (!type) {
    return;
  }
  setTimeout(() => {
    const poiTypeLabel = poiSelection.value.$refs[type];
    let target = poiTypeLabel;
    if (e) {
      const itemId = e.activePoi ? e.id : e.poiId;
      const poiStopLabel = poiSelection.value.$refs[itemId];
      if (poiStopLabel) {
        target = poiStopLabel;
      }
    }
    const targetEl = target[0].$el || target[0];
    targetEl.scrollIntoView({ behavior: 'smooth', block: position, inline: 'end' });
  }, 800);
}
function selectPlace(location) {
  if (!location) {
    return;
  }
  if (prevLocation.value.selectedPoiStopId === location.id) {
    ripple(setLocationButton.value.$el);
  } else {
    prevLocation.value = { ...selectedLocation };
  }
}
function selectMarker(e) {
  if (selectedLocation.selectedPoiStopId === e.id) {
    ripple(setLocationButton.value.$el);
    return;
  }
  if (!vuetify.breakpoint.xs) {
    scrollToPoiSelection(e.type, e, 'center');
  }
  const selectedPoi = props.poiListWithoutGrouping.find((poi) => poi.stops.find((poiStop) => poiStop.poiStopId === e.id));
  selectedPoiType.value = selectedPoi?.type;
  selectedLocation.selectedPoiStopId = e.id;
  emit('updatePoiId', e.id);
  emit('updatePoiType', e.type);
  emit('setError', '');
  setPoi(getSelectedStop.value);
}
function updateZoom(e) {
  zoom.value = e;
}

defineExpose({
  mapKey,
  getWrapperClientHeight,
  setMapFocused,
  scrollToPoiSelection,
  invalidateMapSize,
});
</script>

<style scoped lang="scss">
.action-container {
  width: 50%;
}

.map-container {
  z-index: 1000;

  &.mobile {
    height: 500px;
  }

  &.full-height {
    height: 100%;
  }
}

.poi-list-container {
  z-index: 2;
}

.error-container {
  position: relative;
  border-radius: 0.4rem;
  border: 1px solid var(--v-error-base);
  background: #fff;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.12;
    background-color: var(--v-error-base) !important;
  }
}
</style>
